/**
 * Product creation contracts are system-selected, versioned pre-create rules.
 *
 * The user selects a product (for example PoO); the product resolves to one of
 * these contracts automatically.  The contract id is never a user-facing
 * preset choice and is never trusted from the request.
 */
export const PRODUCT_CREATION_CONTRACT_IDS = ['ordinary-v1', 'poo-v1'] as const

export type ProductCreationContractId = (typeof PRODUCT_CREATION_CONTRACT_IDS)[number]

export interface ProductCreationContractDefinition {
  id: ProductCreationContractId
  /** null is the fallback used by every product without a special contract. */
  product: string | null
  requiresTrustedActor: boolean
  /** Saving this product requires an effective edit PIN from the path's
   * ordinary Protection controls (a directly entered or inherited PIN). */
  requiresEditPin: boolean
  createsProtocol: 'none' | 'poo-v1'
  initialLifecycle: null | 'setup'
  promotesVisitorToMessage: boolean
  /** The contract can be selected from a new message/answer Configure screen,
   * not only from the root-page creator. */
  supportsConfigureCreate: boolean
}

export const ordinaryProductCreationContract: ProductCreationContractDefinition = Object.freeze({
  id: 'ordinary-v1',
  product: null,
  requiresTrustedActor: false,
  requiresEditPin: false,
  createsProtocol: 'none',
  initialLifecycle: null,
  promotesVisitorToMessage: false,
  supportsConfigureCreate: true,
})

export const pooProductCreationContract: ProductCreationContractDefinition = Object.freeze({
  id: 'poo-v1',
  product: 'poo',
  requiresTrustedActor: true,
  requiresEditPin: true,
  createsProtocol: 'poo-v1',
  initialLifecycle: 'setup',
  promotesVisitorToMessage: true,
  supportsConfigureCreate: true,
})

const explicitContracts = new Map<string, ProductCreationContractDefinition>([
  [pooProductCreationContract.product!, pooProductCreationContract],
])

const contractsById = new Map<ProductCreationContractId, ProductCreationContractDefinition>([
  [ordinaryProductCreationContract.id, ordinaryProductCreationContract],
  [pooProductCreationContract.id, pooProductCreationContract],
])

/** Resolve from the selected product or authoritative request msg_type. */
export function productCreationContractForProduct(product: string | null | undefined): ProductCreationContractDefinition {
  return explicitContracts.get(String(product ?? '').toLowerCase()) ?? ordinaryProductCreationContract
}

export function productCreationContractById(id: ProductCreationContractId): ProductCreationContractDefinition {
  const contract = contractsById.get(id)
  if (!contract) throw new Error(`Unknown product creation contract: ${id}`)
  return contract
}

export const UNPINNED_CREATION_WARNING = 'This message has no edit PIN.'
export const POO_EDIT_PIN_REQUIRED_WARNING =
  'Opening Proof Protocol documents require an edit PIN. Add one under Protection before saving.'

export interface CreationEditPinState {
  enabled?: boolean
  value?: unknown
  /** A parent/forced PIN is effective even when the visible input is locked. */
  inherited?: boolean
}

export interface CreationEditPinGuard {
  isUnpinned: boolean
  blocksSave: boolean
  message: string
}

/** Shared warning/guard fact for every creation path. The path still owns how
 * it saves; only contracts such as PoO turn the warning into a hard block. */
export function creationEditPinGuard(
  product: string | null | undefined,
  pin: CreationEditPinState,
): CreationEditPinGuard {
  const directValue = String(pin.value ?? '')
  const hasDirectPin = pin.enabled !== false && directValue !== '' && directValue !== '0'
  const isUnpinned = pin.inherited !== true && !hasDirectPin
  const contract = productCreationContractForProduct(product)
  return {
    isUnpinned,
    blocksSave: isUnpinned && contract.requiresEditPin,
    message: contract.requiresEditPin ? POO_EDIT_PIN_REQUIRED_WARNING : UNPINNED_CREATION_WARNING,
  }
}
